import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Field, TextInput, SectionCard } from "./_shared";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Copy, Send, CheckCircle, XCircle } from "lucide-react";

export default function EditSettings() {
  const { user } = useAuth();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastLink, setLastLink] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    api.get("/auth/last-reset-link").then((r) => setLastLink(r.data.link)).catch(() => {});
    api.get("/auth/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!currentPwd) { toast.error("Informe sua senha atual"); return; }
    if (newPwd && newPwd !== confirmPwd) { toast.error("As novas senhas não coincidem"); return; }
    if (!newEmail && !newPwd) { toast.error("Informe um novo e-mail ou nova senha"); return; }
    setSubmitting(true);
    try {
      const body = { current_password: currentPwd };
      if (newEmail) body.new_email = newEmail;
      if (newPwd) body.new_password = newPwd;
      const { data } = await api.post("/auth/change-credentials", body);
      if (data.token) localStorage.setItem("pt_token", data.token);
      toast.success(data.message || "Atualizado");
      setCurrentPwd(""); setNewEmail(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const onSendTest = async () => {
    if (!testTo) { toast.error("Informe um destinatário"); return; }
    setSendingTest(true);
    try {
      await api.post("/auth/test-email", { to: testTo });
      toast.success(`E-mail de teste enviado para ${testTo}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSendingTest(false); }
  };

  return (
    <div className="space-y-6">
      {/* Email provider status */}
      <SectionCard title="Provedor de E-mail (Resend)">
        {emailStatus === null ? (
          <p className="text-sm text-onyx/60">Verificando…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3" data-testid="email-status-row">
              {emailStatus.configured ? (
                <>
                  <CheckCircle size={20} className="text-gold flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-onyx">Resend configurado</p>
                    <p className="text-xs text-onyx/60">Remetente: <code className="text-gold">{emailStatus.sender}</code></p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle size={20} className="text-destructive flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-onyx">Resend não configurado</p>
                    <p className="text-xs text-onyx/60">
                      Sem chave de API, os e-mails de recuperação não são enviados — o link aparece nos logs e logo abaixo.
                    </p>
                  </div>
                </>
              )}
            </div>

            {!emailStatus.configured ? (
              <div className="bg-alabaster border border-border p-4 text-sm text-onyx/70 space-y-2">
                <p className="font-semibold text-onyx">Como ativar:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Crie uma conta grátis em <a className="text-gold underline" href="https://resend.com" target="_blank" rel="noreferrer">resend.com</a></li>
                  <li>No painel Resend, crie uma <strong>API Key</strong> (começa com <code>re_</code>)</li>
                  <li>Edite o arquivo <code className="text-gold">/app/backend/.env</code> e preencha:
                    <pre className="bg-onyx text-alabaster p-2 mt-1 text-xs overflow-x-auto">RESEND_API_KEY="re_sua_chave_aqui"
SENDER_EMAIL="onboarding@resend.dev"</pre>
                  </li>
                  <li>Reinicie o backend e recarregue esta página</li>
                </ol>
                <p className="text-xs text-onyx/50 pt-2 border-t border-border">
                  No modo gratuito + domínio padrão (<code>resend.dev</code>), você só envia para o e-mail cadastrado na Resend. Para enviar para qualquer destinatário, verifique seu próprio domínio em <a className="text-gold underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>.
                </p>
              </div>
            ) : (
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Field label="Enviar e-mail de teste para">
                    <TextInput value={testTo} onChange={setTestTo} placeholder="seu@email.com" testId="test-email-input" />
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={onSendTest}
                  disabled={sendingTest}
                  data-testid="send-test-email"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-onyx text-alabaster text-xs tracking-[0.2em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors disabled:opacity-50"
                >
                  <Send size={14} /> {sendingTest ? "Enviando…" : "Testar"}
                </button>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Change credentials */}
      <SectionCard title="Alterar Credenciais">
        <form data-testid="settings-form" onSubmit={onSubmit} className="space-y-5">
          <Field label="E-mail atual">
            <input type="email" value={user?.email || ""} readOnly className="w-full px-3 py-2.5 border border-border bg-alabaster text-onyx text-sm cursor-not-allowed" />
          </Field>
          <Field label="Novo e-mail (opcional)">
            <TextInput value={newEmail} onChange={setNewEmail} placeholder="novo@email.com" testId="settings-new-email" />
          </Field>
          <Field label="Nova senha (opcional, mín. 6 caracteres)">
            <input data-testid="settings-new-password" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="w-full px-3 py-2.5 border border-border bg-white text-onyx text-sm focus:outline-none focus:border-gold" />
          </Field>
          {newPwd && (
            <Field label="Confirmar nova senha">
              <input data-testid="settings-confirm-password" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="w-full px-3 py-2.5 border border-border bg-white text-onyx text-sm focus:outline-none focus:border-gold" />
            </Field>
          )}
          <Field label="Senha atual (para autorizar)" hint="Necessária para confirmar qualquer alteração.">
            <input data-testid="settings-current-password" type="password" required value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} className="w-full px-3 py-2.5 border border-border bg-white text-onyx text-sm focus:outline-none focus:border-gold" />
          </Field>
          <button type="submit" disabled={submitting} data-testid="settings-submit" className="inline-flex items-center gap-2 px-6 py-3 bg-onyx text-alabaster text-xs tracking-[0.25em] uppercase font-semibold hover:bg-gold hover:text-onyx transition-colors disabled:opacity-50">
            {submitting ? "Salvando…" : "Atualizar Credenciais"}
          </button>
        </form>
      </SectionCard>

      {/* Last reset link */}
      <SectionCard title="Último Link de Recuperação Disponível">
        <p className="text-sm text-onyx/60 mb-3">
          Quando alguém usa "Esqueci minha senha", geramos um link único válido por 1 hora. Se o Resend estiver configurado, o link é enviado por e-mail. Caso contrário, copie daqui.
        </p>
        {lastLink ? (
          <div className="flex items-center gap-3 bg-alabaster border border-border p-3">
            <code data-testid="last-reset-link" className="text-xs text-gold break-all flex-1">{lastLink}</code>
            <button type="button" data-testid="copy-reset-link" onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Link copiado"); }} className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.2em] border border-onyx hover:bg-onyx hover:text-alabaster transition-colors">
              <Copy size={12} /> Copiar
            </button>
          </div>
        ) : (
          <p data-testid="no-reset-link" className="text-sm text-onyx/40 italic">Nenhum link de recuperação ativo no momento.</p>
        )}
      </SectionCard>
    </div>
  );
}
